import React, {
  useContext, useState, useRef,
} from 'react';
import { NavLink } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';

import { useHeaderFooter } from '../../hooks';
import { AppDataContext } from '../../context';
import {
  SpinLoader, UserEditPopUp, CoursesInProgress, AchievementDisplay,
} from '../../components';
import type { AuthenticatedUser } from '../../types';
import { setBirthDate, uploadImage } from './utils';

import './index.scss';
import messages from './messages';

import cameraImg from '../../img/camera.svg';
import line65Img from '../../img/line-6-5.svg';
import editImg from '../../img/dashboard/edit.svg';
import line131Img from '../../img/dashboard/line-13-1.svg';

interface DashboardProps {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardProps> = ({ children }) => {
  const { authenticatedUser, setAuthenticatedUser } = useContext(AppDataContext);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const intl = useIntl();
  const { headerContent, footerContent } = useHeaderFooter(children);
  const {
    name, username, profileImage, bio, email, socialLinks, yearOfBirth,
  } = authenticatedUser ?? {};

  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const selectedFile = event.target.files?.[0];
    if (selectedFile && username) {
      if (!yearOfBirth) {
        await setBirthDate(selectedFile, username, intl);
        window.location.reload();
        return;
      }
      await uploadImage(selectedFile, username, intl);
      window.location.reload();
    }
  };

  return (
    <>
      <input
        type="hidden"
        id="anPageName"
        name="page"
        defaultValue="user-dashboard"
      />
      <div className="container-center-horizontal">
        <div className="user-dashboard screen">
          {headerContent}
          <div className="page-nav recklessneue-book-normal-congress-blue-22-8px">
            <div className="flex-col-1 flex-col-5">
              <div className="dashboard">{intl.formatMessage(messages.dashboard)}</div>
              <img className="line-6 line" src={line65Img} alt="Line 6" />
            </div>
            <div className="view-public-profile">
              <NavLink
                id="profile-link"
                to="/profile-learner"
              >
                {intl.formatMessage(messages.viewPublicProfile)}
              </NavLink>
            </div>
          </div>
          <div className="flex-row flex w-100 dashboard-info-container">
            {authenticatedUser && (
              <div className="personal-info">
                <div className="overlap-group7">
                  <div className="my-personal-information inter-light-congress-blue-22px">
                    {intl.formatMessage(messages.myPersonalInformation)}
                    <button
                      type="button"
                      className="learner-profile-edit-button"
                      onClick={() => setIsProfileModalOpen(true)}
                      aria-label={intl.formatMessage(messages.editButtonAlt)}
                    >
                      <img
                        className="edit"
                        src={editImg}
                        alt={intl.formatMessage(messages.editButtonAlt)}
                      />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="headshot public-profile-image-container"
                    onClick={handleProfileImageClick}
                    style={{ visibility: 'visible' }}
                  >
                    {isLoading ? (
                      <SpinLoader size={40} containerHeight="150px" />
                    ) : (
                      <img
                        className="image-9 user-img"
                        src={`${profileImage?.imageUrlFull}`}
                        alt={intl.formatMessage(messages.profileImageAlt)}
                      />
                    )}
                    <div className="profile-upload-btn">
                      <img className="profile-upload-btn-icon" src={cameraImg} alt="" />
                    </div>
                  </button>
                  <h1 className="title c-name">{name}</h1>
                  <input
                    type="file"
                    id="profileImageInput"
                    name="imageInput"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <img
                    className="line-13 line"
                    src={line131Img}
                    alt="Line 13"
                  />
                  <div className="personal-information">
                    <p className="email-saanviklwc neuehaasgroteskdisplaypro-medium-congress-blue-24px">
                      <span className="neuehaasgroteskdisplaypro-medium-congress-blue-24px">
                        {intl.formatMessage(messages.emailLabel)}:
                      </span>{' '}
                      <span className="neuehaasgroteskdisplaypro-light-congress-blue-24px c-email">
                        {email}
                      </span>
                      <br />
                      <span className="neuehaasgroteskdisplaypro-medium-congress-blue-24px">
                        {intl.formatMessage(messages.bioLabel)}:
                      </span>{' '}
                      <span className="neuehaasgroteskdisplaypro-light-congress-blue-24px c-bio">
                        {bio}
                      </span>
                      <br />
                      <span className="neuehaasgroteskdisplaypro-medium-congress-blue-24px">
                        {intl.formatMessage(messages.linksLabel)}:
                      </span>{' '}
                      <span className="neuehaasgroteskdisplaypro-light-congress-blue-24px">
                        {(() => {
                          const xLink = socialLinks?.find((link: { platform: string }) => link?.platform === 'x')?.socialLink
                            || socialLinks?.find((link: { platform: string }) => link?.platform === 'twitter')?.socialLink;
                          return xLink && (
                            <>
                              <a
                                data-name="x"
                                href={xLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {' '}
                                {intl.formatMessage(messages.xSocialLabel)}{' '}
                              </a>
                              ,{' '}
                            </>
                          );
                        })()}
                        {socialLinks?.find((link: { platform: string }) => link?.platform === 'linkedin')?.socialLink && (
                          <a
                            data-name="linkedin"
                            href={socialLinks?.find((link: { platform: string }) => link?.platform === 'linkedin')?.socialLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {intl.formatMessage(messages.linkedInLabel)}{' '}
                          </a>
                        )}
                      </span>
                      <br />
                      <span className="neuehaasgroteskdisplaypro-medium-congress-blue-24px">
                        {intl.formatMessage(messages.profileAccessLabel)}:
                      </span>{' '}
                      <span className="neuehaasgroteskdisplaypro-light-congress-blue-24px">
                        {intl.formatMessage(messages.profileAccessValue)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="achievement-container">
              <div className="my-achievements">
                <div className="overlap-group6">
                  <AchievementDisplay textHelper={intl.formatMessage(messages.certificateHelper)} />
                </div>
              </div>
              <div className="my-achievements">
                <div className="overlap-group6">
                  <AchievementDisplay
                    textHelper={intl.formatMessage(messages.badgeHelper)}
                    title={intl.formatMessage(messages.badges)}
                    displayCertificates={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <CoursesInProgress />
          <CoursesInProgress showOnProgress={false} title={intl.formatMessage(messages.modulesCompleted)} />
          {footerContent}
          {isProfileModalOpen && (
            <UserEditPopUp
              userMetadata={authenticatedUser}
              setIsProfileModalOpen={setIsProfileModalOpen}
              setUserMetadata={(updatedUser: AuthenticatedUser) => {
                setAuthenticatedUser(updatedUser);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
